import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { BaseViewportPlugin, useFramework } from '@/framework';
import { PluginType, type IExportPlugin } from '@/version-types';
import { StageToolbar } from './stage-toolbar';
import stageToolbarState, { useStageToolbarState } from './states/stage-toolbar-state';
import './plugin.less';

const StageToolbarView = () => {
    const framework = useFramework();
    const toolbar = framework.service.registration.getInstance<StageToolbar>('StageToolbar');
    const visible = useStageToolbarState()((state) => state.visible);
    const config = useStageToolbarState()((state) => state.config);
    const items = useStageToolbarState()((state) => state.items);
    const activeItemKey = useStageToolbarState()((state) => state.activeItemKey);
    const [pointerInsideToolbar, setPointerInsideToolbar] = useState(false);
    // 条目通过 toolbar 实例同步读取 availability，这里订阅状态只负责让壳在可用性变化时重新渲染条目。
    useStageToolbarState()((state) => state.itemAvailability);

    const sortedItems = useMemo(() => {
        // itemOrder 只提升显式配置的条目；未配置的条目继续沿用注册时的 sort 顺序。
        const order = new Map(config.itemOrder?.map((key, index) => [key, index]));
        return [...items].sort((left, right) => {
            const leftOrder = order.get(left.key);
            const rightOrder = order.get(right.key);
            if (leftOrder !== undefined || rightOrder !== undefined) {
                return (leftOrder ?? Number.MAX_SAFE_INTEGER) - (rightOrder ?? Number.MAX_SAFE_INTEGER);
            }
            return left.sort - right.sort;
        });
    }, [config.itemOrder, items]);

    useEffect(() => {
        const type = activeItemKey || '';
        toolbar.emitPanelVisibleChange(type);
        // 外部传入的位置只对本次图层面板生效，关闭后必须清空，避免下次由用户打开时沿用旧位置。
        if (activeItemKey !== 'hierarchy') toolbar.setHierarchyPanelPosition({});
    }, [activeItemKey, toolbar]);

    if (!visible) return null;

    return (
        <BaseViewportPlugin name="stage-toolbar" style={{ overflow: 'initial', zIndex: 99 }}>
            <div
                className={classNames('c-stage-toolbar', {
                    'c-stage-toolbar-center': config.position === 'center',
                    'c-stage-toolbar-compact': config.appearance === 'compact',
                })}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div
                    className="l-navigator no-preview"
                    onMouseEnter={() => setPointerInsideToolbar(true)}
                    onMouseLeave={() => setPointerInsideToolbar(false)}
                >
                    {sortedItems.map((item) => (
                        <span className="stage-toolbar-item" data-item-key={item.key} key={item.key}>
                            {item.render({
                                framework,
                                toolbar,
                                config,
                                active: activeItemKey === item.key,
                                pointerInsideToolbar,
                                setActive(active) {
                                    if (item.key === 'hierarchy') {
                                        // 图层入口需要额外上报来源，供业务区分用户点击与 API 控制。
                                        toolbar.setHierarchyVisible(active, { source: 'user' });
                                    } else {
                                        toolbar.setActiveItem(active ? item.key : null);
                                    }
                                },
                            })}
                        </span>
                    ))}
                </div>
            </div>
        </BaseViewportPlugin>
    );
};

const exportPlugin: IExportPlugin = {
    type: PluginType.VIEWPORT,
    sort: 1,
    name: 'stage-toolbar',
    createState() {
        return [stageToolbarState];
    },
    createInstance({ identity }) {
        return [{ key: 'StageToolbar', value: new StageToolbar(identity) }];
    },
    render() {
        return <StageToolbarView />;
    },
};

export default exportPlugin;
